import { createContext, useContext, useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

const BrandContext = createContext(null);
export const useBrand = () => useContext(BrandContext);

function hexToRgb(hex) {
  const h = (hex || "#999999").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

const mixWithWhite = (rgb, amt) => ({
  r: Math.round(rgb.r * (1 - amt) + 255 * amt),
  g: Math.round(rgb.g * (1 - amt) + 255 * amt),
  b: Math.round(rgb.b * (1 - amt) + 255 * amt),
});

const darken = (rgb, amt) => ({
  r: Math.max(0, Math.round(rgb.r * (1 - amt))),
  g: Math.max(0, Math.round(rgb.g * (1 - amt))),
  b: Math.max(0, Math.round(rgb.b * (1 - amt))),
});

function relativeLuminance(rgb) {
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

const hslStr = ({ h, s, l }) => `${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%`;
const hexStr = (rgb) => `#${[rgb.r, rgb.g, rgb.b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;

export function applyBrandVars(marca) {
  const root = document.documentElement.style;
  const primaryRgb = hexToRgb(marca.color_primario);
  const primaryHsl = rgbToHsl(primaryRgb);
  const lum = relativeLuminance(primaryRgb);
  const sobreEsOscuro = lum > 0.55;

  root.setProperty("--primary", hslStr(primaryHsl));
  root.setProperty("--primary-foreground", sobreEsOscuro ? "0 0% 7%" : "0 0% 100%");
  root.setProperty("--ring", hslStr(primaryHsl));

  root.setProperty("--marca-primario", marca.color_primario);
  root.setProperty("--marca-primario-hover", hexStr(darken(primaryRgb, 0.1)));
  root.setProperty("--marca-primario-suave", hexStr(mixWithWhite(primaryRgb, 0.9)));
  root.setProperty("--marca-primario-borde", hexStr(mixWithWhite(primaryRgb, 0.7)));
  root.setProperty("--marca-sobre-primario", sobreEsOscuro ? "#111111" : "#FFFFFF");
  root.setProperty("--marca-acento", marca.color_acento || marca.color_primario);

  document.title = marca.nombre_comercial || "Marthitashop";
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute("content", marca.color_primario || "#999999");
  if (marca.logo_icono_url) {
    const icon = document.querySelector('link[rel="icon"]');
    if (icon) icon.setAttribute("href", `${BACKEND_URL}${marca.logo_icono_url}`);
  }
}

export function BrandProvider({ children }) {
  const [marca, setMarca] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/marca`)
      .then((r) => r.json())
      .then((m) => { applyBrandVars(m); setMarca(m); })
      .catch(() => setMarca({}));
  }, []);

  if (!marca) return <div style={{ minHeight: "100vh", background: "#faf7f6" }} />;
  return <BrandContext.Provider value={marca}>{children}</BrandContext.Provider>;
}
